import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Label, RadioGroup, RadioGroupItem } from "@hulahoop/ui";
import type { InvestorWizardFormData } from "@/domain/validation/investorSchemas";
import { CreateInvestorCompanyStep } from "./CreateInvestorCompanyStep";
import { CreateInvestorContactStep } from "./CreateInvestorContactStep";
import { CreateInvestorExtraStep } from "./CreateInvestorExtraStep";
import { WizardTextField } from "./WizardTextField";

export function CreateInvestorStepFields({ step }: { step: number }) {
  const { t } = useTranslation();
  const form = useFormContext<InvestorWizardFormData>();
  if (step === 0) {
    return (
      <Controller
        control={form.control}
        name="type"
        render={({ field }) => (
          <RadioGroup
            value={field.value}
            onValueChange={(value) => field.onChange(value)}
            aria-label={t("investors.wizard.type_label")}
            className="gap-(--card-gap)"
          >
            {(["individual", "company"] as const).map((option) => (
              <div key={option} className="flex items-start gap-3">
                <RadioGroupItem value={option} id={`investor-type-${option}`} />
                <Label
                  htmlFor={`investor-type-${option}`}
                  className="flex flex-col items-start gap-0.5 text-left"
                >
                  <span>{t(`investors.wizard.${option}`)}</span>
                  <span className="text-xs font-normal text-muted-foreground">
                    {t(`investors.wizard.${option}_hint`)}
                  </span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        )}
      />
    );
  }
  if (step === 1) {
    return form.watch("type") === "company" ? (
      <CreateInvestorCompanyStep />
    ) : (
      <>
        <div className="grid gap-(--card-gap) sm:grid-cols-2">
          <WizardTextField
            name="firstName"
            label="investors.editor.first_name"
            placeholder="investors.wizard.placeholders.first_name"
            required
          />
          <WizardTextField
            name="lastName"
            label="investors.editor.last_name"
            placeholder="investors.wizard.placeholders.last_name"
            required
          />
        </div>
        <div className="grid gap-(--card-gap) sm:grid-cols-2">
          <WizardTextField
            name="dni"
            label="investors.attributes.tax_id"
            placeholder="investors.wizard.placeholders.dni"
            required
          />
          <WizardTextField
            name="profession"
            label="investors.attributes.profession"
            placeholder="investors.wizard.placeholders.profession"
          />
        </div>
      </>
    );
  }
  return step === 2 ? <CreateInvestorContactStep /> : <CreateInvestorExtraStep />;
}
